import getFrontConfig from '../common/getFrontConfig';
import { detectPageType, urlWithSearchParams } from '../common/utils/url';
import { isObject } from '../common/utils/type';
import { getOrCreateElement } from '../common/utils/dom';
import { initCookieTarget } from '../common/utils/cookieUtils';

const POPUP_CONTAINER_ID = 'popupByl';
const POPUP_CONTAINER_SELECTOR = `#${POPUP_CONTAINER_ID}`;
const { popupConfig, urls: { pageShow } } = getFrontConfig();

/** Clear the popup container contents */
function clearContainer() {
  const container = document.querySelector(POPUP_CONTAINER_SELECTOR);
  if (container) container.innerHTML = '';
}

/**
 * Determine whether the popup should be shown for the current page identifier.
 * Includes take priority: if non-empty, only listed IDs are shown regardless of excludes.
 * When includes is empty, all IDs are shown except those in excludes.
 * @param {string[]} includes
 * @param {string[]} excludes
 * @param {string} currentId - product ID (PDP) or category ID (PLP)
 * @returns {boolean}
 */
function shouldShowPopup(includes, excludes, currentId) {
  if (includes.length > 0) return includes.includes(currentId);
  return !excludes.includes(currentId);
}

/**
 * Resolve the popup page ID for the current route, applying include/exclude
 * filtering for PDP and PLP. Returns null if no popup should be shown.
 * @param {'home'|'pdp'|'plp'} pageType
 * @param {import('vue-router').Route} route
 * @returns {string|null}
 */
function getPopupId(pageType, route) {
  const config = popupConfig[pageType];
  if (!isObject(config) || !config.id) return null;
  const { id, includes = [], excludes = [] } = config;
  const currentId = route.params.pid || route.params.id;
  return shouldShowPopup(includes, excludes, currentId) ? id : null;
}

let lastPopupId = null;

/**
 * Fetch the popup HTML from Page-Show, inject it into the container and
 * initialise the close-button cookie behaviour.
 * Returns true on success, false on any network or server error.
 * @param {string} popupId - Page Designer page ID
 * @returns {Promise<boolean>}
 */
async function loadPopup(popupId) {
  try {
    const url = urlWithSearchParams(pageShow, { cid: popupId });
    const response = await fetch(url);
    if (!response.ok) return false;

    const html = await response.text();
    const container = getOrCreateElement(POPUP_CONTAINER_SELECTOR);
    container.id = POPUP_CONTAINER_ID;
    container.innerHTML = html;
    initCookieTarget(container.querySelector('[data-cookie-name]'));
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Evaluate whether a popup should be shown for the given route and load it.
 * If the same popup is already displayed it is kept as-is (no re-fetch).
 * On load failure lastPopupId is not updated so the next navigation retries.
 * @param {import('vue-router').Route} route - the destination route from Vue Router afterEach
 */
export async function evaluate(route) {
  const pageType = detectPageType(route.name, route.fullPath);
  if (!pageType) {
    clearContainer();
    lastPopupId = null;
    return;
  }

  const popupId = getPopupId(pageType, route);
  if (!popupId) {
    clearContainer();
    lastPopupId = null;
    return;
  }

  if (popupId === lastPopupId) return;

  const loaded = await loadPopup(popupId);
  if (loaded) lastPopupId = popupId;
}
